import {css} from 'aphrodite';
import React, {useEffect} from 'react';
import isEqual from 'react-fast-compare';
import {debounce} from 'ts-debounce';

import {updateAndRunAnalysisStep} from 'analysis-tool/AnalysisActions';
import {
  RegressionStepDataName,
  SINGLE_CHART_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {findNullableStepData} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import {GetChartViewDataFrame_dataFrame_DataFrameNode as VisualizationStepEditorDataFrameNode} from 'analysis-tool/ui/main-view/__generated/GetChartViewDataFrame';
import {assert} from 'Asserts';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import {isVisualizationTypeParameters} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import {
  VisualizationConfig,
  VisualizationType,
} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {REGRESSION_FINISHED_EVENT, REGRESSION_STARTED_EVENT} from 'performance/processAnalysesEvents';
import {NOT_SPECIFIED} from 'performance/ProfilerConstants';
import hasDownstreamFlagRowsStep from 'pipelines/hasDownstreamFlagRowsStep';
import * as PipelineDAO from 'pipelines/network/PipelineDAO';
import {PipelineGraphQL, PipelineStepConfig} from 'pipelines/PipelineTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import {RegressionAnalysis} from 'ui-library/charting/statistics/regression/RegressionTypes';
import {SuccessfulAnalysis} from 'ui-library/charting/statistics/Types';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import usePrevious from 'ui-library/hooks/usePrevious';
import {SaveState} from 'ui-library/SaveStatusIndicator';
import {isServerRequestError} from 'util/Net';
import {withLoadingScreen} from 'util/Promises';
import {MakeAllFieldsOptional} from 'util/Types';

import AdditionalEditorSection from '../components/AdditionalEditorSection';
import AddTransformStepDropdown, {
  BLOCKED_BY_DOWNSTREAM_FLAG_ROWS_MESSAGE,
  FIX_FAILED_TRANSFORMS_MESSAGE,
  WAITING_FOR_STEP_SAVE_MESSAGE,
} from './AddTransformStepDropdown';
import RegressionEditorSidebarSection, {
  runConfirmCloseRegressionModal,
} from './editor-sections/RegressionEditorSidebarSection';
import VisualizationEditorSkeleton, {
  VisualizationEditorSkeletonProps,
  visualizationEditorSkeletonStyles,
} from './editor-sections/VisualizationEditorSkeleton';
import {getDefaultVisualizationConfigFromRegression} from './getDefaultVisualizationConfig';
import RegressionDataFramesPreview from './RegressionDataFramesPreview';
import {
  GetRegressionDataFrames,
  GetRegressionDataFrames_modelOutputDataFrame_DataFrameNode as GetRegressionOutputDataFrame,
  GetRegressionDataFrames_modelVariablesDataFrame_DataFrameNode as GetRegressionVariablesDataFrame,
} from './regressions/__generated/GetRegressionDataFrames';
import getPersistedRegressionAnalyses, {
  GET_REGRESSION_DATA_FRAMES_QUERY,
} from './regressions/getPersistedRegressionAnalyses';
import {getTransformPills} from './TransformPillUtils';

type DraftRegressionConfig = MakeAllFieldsOptional<
  PipelineStepConfig<PipelineStepType.REGRESSION, 0>['regressionConfig']
>;
interface RegressionStepEditorState {
  nullableVisualizationConfig: VisualizationConfig | null;
  nullableRegressionName: string | null;
  nullableDraftRegressionConfig: DraftRegressionConfig | null;
  saveState: SaveState.SAVED | SaveState.SAVING | SaveState.FAILED;
  shouldDisplayRegressionLines: boolean;
  isReadOnly: boolean;
}

const regressionStepEditorGlobalState = makeUseGlobalClientState<RegressionStepEditorState>({
  nullableVisualizationConfig: null,
  nullableRegressionName: null,
  nullableDraftRegressionConfig: null,
  saveState: SaveState.SAVED,
  shouldDisplayRegressionLines: true,
  isReadOnly: false,
});

const debouncedUpdateRegressionConfig = debounce(
  async (clientStateId: string, analysisId: string, stepId: string, regressionName: string) => {
    try {
      await PipelineDAO.updatePipelineStep(analysisId, {
        stepId,
        stepConfig: {name: regressionName},
      });
      regressionStepEditorGlobalState.updateState(clientStateId, {
        ...regressionStepEditorGlobalState.getState(clientStateId),
        saveState: SaveState.SAVED,
      });
    } catch (err) {
      regressionStepEditorGlobalState.updateState(clientStateId, {
        ...regressionStepEditorGlobalState.getState(clientStateId),
        saveState: SaveState.FAILED,
      });
      if (!isServerRequestError(err)) {
        throw err;
      }
    }
  },
  200
);

function isRegressionConfigComplete(
  regressionConfig: DraftRegressionConfig
): regressionConfig is PipelineStepConfig<PipelineStepType.REGRESSION, 0>['regressionConfig'] {
  return ['modelType', 'xColumn', 'yColumn'].every((field) => regressionConfig.hasOwnProperty(field));
}

function getRegressionConfigValuesFromVisualizationConfig(
  nullableVisualizationConfig: VisualizationConfig | null
): DraftRegressionConfig {
  if (nullableVisualizationConfig == null) {
    return {};
  }

  assert(
    isVisualizationTypeParameters(nullableVisualizationConfig.parameters, VisualizationType.SCATTER_PLOT),
    'Expected a scatter plot visualization config.'
  );

  return {
    xColumn: nullableVisualizationConfig.parameters.xAxisColumnName,
    yColumn: nullableVisualizationConfig.parameters.yAxisColumnName,
    partitionColumn: nullableVisualizationConfig.parameters.seriesColumnName,
  };
}

function isInvalidRegressionName(regressionName: string): boolean {
  return !regressionName || regressionName.match(/^\s*$/) != null;
}

function isDuplicateName(
  analysis: PipelineGraphQL,
  regressionStep: GetAnalysisPipelineStep,
  name: string
): boolean {
  // TODO(BNCH-97973): Limit duplicate check just to regressions under the top-level table, rather than to all regressions
  // in the analysis.
  const otherRegressionSteps = analysis.execution.steps.filter(
    (step) => step.definition.stepType === PipelineStepType.REGRESSION && step.id !== regressionStep.id
  );

  return otherRegressionSteps.some(
    (step) => step.config?.variables.find((variable) => variable.name === 'name')?.value === name
  );
}

function renderPreviewDataFrames(
  state: {analysis: PipelineGraphQL; clientStateId: string; regressionStep: GetAnalysisPipelineStep},
  inputDataFrame: VisualizationStepEditorDataFrameNode,
  modelOutputDataFrame: GetRegressionOutputDataFrame | null,
  modelVariablesDataFrame: GetRegressionVariablesDataFrame | null,
  isExpandedPreviewState: boolean,
  setIsExpandedPreviewState: (newState: boolean) => void
): JSX.Element {
  const {analysis, clientStateId, regressionStep} = state;

  return (
    <RegressionDataFramesPreview
      analysis={analysis}
      clientStateId={clientStateId}
      inputDataFrame={inputDataFrame}
      isExpandedPreviewState={isExpandedPreviewState}
      modelOutputDataFrame={modelOutputDataFrame}
      modelVariablesDataFrame={modelVariablesDataFrame}
      regressionStep={regressionStep}
      setIsExpandedPreviewState={setIsExpandedPreviewState}
    />
  );
}

function renderEditorSidebarChildren(
  state: {
    analysis: PipelineGraphQL;
    clientStateId: string;
    regressionStep: GetAnalysisPipelineStep;
    inputDataFrameId: string;
    visualizationConfig: VisualizationConfig | null;
    regressionStepEditorState: RegressionStepEditorState;
    setRegressionStepEditorState: (newRegressionStepEditorState: RegressionStepEditorState) => void;
    viewSteps: Array<GetAnalysisPipelineStep>;
    addNewStepsDisabledMessage?: string;
    canAddNewSteps: boolean;
    draftRegressionConfig: DraftRegressionConfig;
  },
  dataFrameNode: VisualizationStepEditorDataFrameNode,
  invalidatePreview: () => void
): JSX.Element {
  const {
    analysis,
    clientStateId,
    regressionStep,
    inputDataFrameId,
    visualizationConfig,
    regressionStepEditorState,
    setRegressionStepEditorState,
    viewSteps,
    addNewStepsDisabledMessage,
    canAddNewSteps,
    draftRegressionConfig,
  } = state;

  async function handleRunRegression(config: DraftRegressionConfig): Promise<void> {
    assert(
      isRegressionConfigComplete(config),
      'Regression config should be complete or button should be disabled.'
    );

    logProfilerTimingEvent(REGRESSION_STARTED_EVENT);

    await withLoadingScreen(
      updateAndRunAnalysisStep(analysis.id, {
        stepId: regressionStep.id,
        stepConfig: {
          regressionConfig: config,
        },
      }),
      'Running regression...'
    );

    invalidatePreview();
    setRegressionStepEditorState({
      ...regressionStepEditorState,
      shouldDisplayRegressionLines: true,
      isReadOnly: true,
    });
  }

  function handleCancelRegression(): void {
    setRegressionStepEditorState({
      ...regressionStepEditorState,
      nullableDraftRegressionConfig:
        regressionStep.config?.variables.find((variable) => variable.name === 'regressionConfig')?.value ??
        null,
      nullableVisualizationConfig: getDefaultVisualizationConfigFromRegression(regressionStep, null),
      isReadOnly: true,
    });
  }

  return (
    <>
      <RegressionEditorSidebarSection
        analysis={analysis}
        clientStateId={clientStateId}
        regressionStep={regressionStep}
        regressionConfig={draftRegressionConfig}
        setRegressionConfig={(newDraftRegressionConfig: DraftRegressionConfig | null) => {
          setRegressionStepEditorState({
            ...regressionStepEditorState,
            nullableDraftRegressionConfig: {
              ...getRegressionConfigValuesFromVisualizationConfig(visualizationConfig),
              ...newDraftRegressionConfig,
            },
            shouldDisplayRegressionLines: false,
          });
        }}
        inputDataFrame={dataFrameNode}
        runButtonIsDisabled={!isRegressionConfigComplete(draftRegressionConfig)}
        onRunRegression={handleRunRegression}
        onCancelRegression={handleCancelRegression}
        isReadOnly={regressionStepEditorState.isReadOnly}
        setIsReadOnly={(newIsReadOnly) => {
          setRegressionStepEditorState({...regressionStepEditorState, isReadOnly: newIsReadOnly});
        }}
      />
      <AdditionalEditorSection
        title="Filters & Transforms"
        addItemElement={
          <AddTransformStepDropdown
            viewSteps={viewSteps}
            dataFrameSpec={dataFrameNode.randomSubsetDataFrameJson}
            inputDataFrameId={inputDataFrameId}
            analysis={analysis}
            canAddNewSteps={canAddNewSteps}
            addNewStepsDisabledMessage={addNewStepsDisabledMessage}
          />
        }
        pills={getTransformPills(analysis, viewSteps)}
        showTopBorder={true}
      />
    </>
  );
}

function RegressionQueryingVisualizationEditorSkeleton({
  regressionStep,
  modelOutputDataFrameId,
  modelVariablesDataFrameId,
  shouldDisplayRegressionLines,
  ...props
}: Omit<VisualizationEditorSkeletonProps, 'regressionAnalyses' | 'renderPreviewDataFrames'> & {
  regressionStep: GetAnalysisPipelineStep;
  modelOutputDataFrameId: string | null;
  modelVariablesDataFrameId: string | null;
  shouldDisplayRegressionLines: boolean;
}): JSX.Element {
  const outputDataFramesQueryResult = useBenchlingQuery<GetRegressionDataFrames>(
    GET_REGRESSION_DATA_FRAMES_QUERY,
    {variables: {modelOutputDataFrameId, modelVariablesDataFrameId}}
  );

  let regressionAnalyses: Array<SuccessfulAnalysis<RegressionAnalysis>> | undefined;
  let renderPreview: (
    inputDataFrame: VisualizationStepEditorDataFrameNode,
    isExpandedPreviewState: boolean,
    setIsExpandedPreviewState: (newState: boolean) => void
  ) => JSX.Element;

  if (
    modelOutputDataFrameId != null &&
    modelVariablesDataFrameId != null &&
    outputDataFramesQueryResult.type === QueryResultType.SUCCESS
  ) {
    const {modelOutputDataFrame, modelVariablesDataFrame} = outputDataFramesQueryResult.data;
    assert(!isInaccessible(modelOutputDataFrame), 'User does not have access to output data frame');
    assert(!isInaccessible(modelVariablesDataFrame), 'User does not have access to variables data frame');

    regressionAnalyses = shouldDisplayRegressionLines
      ? getPersistedRegressionAnalyses(modelVariablesDataFrame)
      : undefined;
    renderPreview = (inputDataFrame, isExpandedPreviewState, setIsExpandedPreviewState) =>
      renderPreviewDataFrames(
        {analysis: props.analysis, clientStateId: props.clientStateId, regressionStep},
        inputDataFrame,
        modelOutputDataFrame,
        modelVariablesDataFrame,
        isExpandedPreviewState,
        setIsExpandedPreviewState
      );
  } else {
    renderPreview = (inputDataFrame, isExpandedPreviewState, setIsExpandedPreviewState) =>
      renderPreviewDataFrames(
        {analysis: props.analysis, clientStateId: props.clientStateId, regressionStep},
        inputDataFrame,
        null,
        null,
        isExpandedPreviewState,
        setIsExpandedPreviewState
      );
  }

  return (
    <VisualizationEditorSkeleton
      {...props}
      regressionAnalyses={regressionAnalyses}
      renderPreviewDataFrames={renderPreview}
    />
  );
}

export interface RegressionStepEditorProps {
  analysis: PipelineGraphQL;
  viewSteps: Array<GetAnalysisPipelineStep>;
  clientStateId: string;
  inputDataFrameId: string;
  onCloseRegression: () => void;
}

export default function RegressionStepEditor({
  analysis,
  viewSteps,
  clientStateId,
  inputDataFrameId,
  onCloseRegression,
}: RegressionStepEditorProps): JSX.Element {
  const regressionStep = viewSteps[viewSteps.length - 1];
  assert(
    regressionStep.definition.stepType === PipelineStepType.REGRESSION,
    'Expected this component to only be used with a regression step.'
  );

  const persistedRegressionName =
    regressionStep.config?.variables.find((variable) => variable.name === 'name')?.value ?? null;
  const persistedRegressionConfig: DraftRegressionConfig | null =
    regressionStep.config?.variables.find((variable) => variable.name === 'regressionConfig')?.value ?? null;

  // Measure the time it takes between when a user has applied a regression and when the step has finished
  // running.
  const statusCode = regressionStep.statusCode;
  const previousStatusCode = usePrevious(statusCode);

  useEffect(() => {
    if (
      [PipelineStepStatusCode.SUCCEEDED, PipelineStepStatusCode.FAILED].includes(statusCode) &&
      previousStatusCode === PipelineStepStatusCode.RUNNING
    ) {
      logProfilerTimingEvent(REGRESSION_FINISHED_EVENT, {
        distributionMetadata: {
          regression_type: persistedRegressionConfig?.modelType?.toLowerCase() ?? NOT_SPECIFIED,
          status_code: statusCode.toLowerCase(),
        },
      });
    }
  }, [persistedRegressionConfig, previousStatusCode, statusCode]);

  const [regressionStepEditorState, setRegressionStepEditorState] =
    regressionStepEditorGlobalState.useGlobalClientState(clientStateId);
  const visualizationConfig = getDefaultVisualizationConfigFromRegression(
    regressionStep,
    regressionStepEditorState.nullableVisualizationConfig
  );
  const regressionName =
    regressionStepEditorState.nullableRegressionName ?? persistedRegressionName ?? 'Regression';
  const draftRegressionConfig = regressionStepEditorState.nullableDraftRegressionConfig ??
    persistedRegressionConfig ?? {
      ...getRegressionConfigValuesFromVisualizationConfig(visualizationConfig),
    };

  const chartStepData = findNullableStepData(
    viewSteps[viewSteps.length - 1],
    SINGLE_CHART_OUTPUT_STEP_DATA_NAME,
    false
  );
  const hasChartOutput = chartStepData != null ? chartStepData.charts.length > 0 : false;

  const allStepsSucceeded = viewSteps
    .slice(0, viewSteps.length - 1)
    .every((step) => step.statusCode === PipelineStepStatusCode.SUCCEEDED);
  const currentStepSaved = regressionStepEditorState.saveState === SaveState.SAVED;
  const blockedByDownstream = hasDownstreamFlagRowsStep(analysis, regressionStep);

  const canAddNewSteps = allStepsSucceeded && currentStepSaved && !blockedByDownstream;
  const addNewStepsDisabledMessage =
    (!allStepsSucceeded ? FIX_FAILED_TRANSFORMS_MESSAGE : undefined) ??
    (!currentStepSaved ? WAITING_FOR_STEP_SAVE_MESSAGE : undefined) ??
    (blockedByDownstream ? BLOCKED_BY_DOWNSTREAM_FLAG_ROWS_MESSAGE : undefined);

  function handleChangeVisualizationName(newName: string): void {
    const validName = !isInvalidRegressionName(newName);

    setRegressionStepEditorState({
      ...regressionStepEditorState,
      nullableRegressionName: newName,
      ...(validName ? {saveState: SaveState.SAVING} : {}),
    });

    if (validName) {
      debouncedUpdateRegressionConfig(clientStateId, analysis.id, regressionStep.id, newName);
    }
  }

  function handleChangeVisualizationConfig(newVisualizationConfig: AnalyticsVisualization): void {
    assert(
      isVisualizationTypeParameters(newVisualizationConfig.parameters, VisualizationType.SCATTER_PLOT),
      'Expected a scatter plot visualization config.'
    );

    // Update the regression config with the shared values
    const newDraftRegressionConfig: DraftRegressionConfig = {
      ...draftRegressionConfig,
      ...getRegressionConfigValuesFromVisualizationConfig(newVisualizationConfig),
    };

    setRegressionStepEditorState({
      ...regressionStepEditorState,
      nullableVisualizationConfig: newVisualizationConfig,
      nullableDraftRegressionConfig: newDraftRegressionConfig,
      shouldDisplayRegressionLines: false,
    });
  }

  async function handleCloseRegression(): Promise<void> {
    const currentPersistedRegressionConfig =
      regressionStep.config?.variables.find((variable) => variable.name === 'regressionConfig')?.value ??
      null;

    if (
      regressionStepEditorState.nullableDraftRegressionConfig != null &&
      !isEqual(currentPersistedRegressionConfig, regressionStepEditorState.nullableDraftRegressionConfig)
    ) {
      await runConfirmCloseRegressionModal('Return to analysis?');
    }

    setRegressionStepEditorState({
      ...regressionStepEditorState,
      nullableVisualizationConfig: null,
      nullableRegressionName: null,
      nullableDraftRegressionConfig: null,
      shouldDisplayRegressionLines: true,
      isReadOnly: false,
    });

    onCloseRegression();
  }

  const modelOutputDataFrameId =
    regressionStep.outputStepDatas.find((stepData) => stepData.name === RegressionStepDataName.MODEL_OUTPUT)
      ?.dataFrames[0]?.id ?? null;
  const modelVariablesDataFrameId =
    regressionStep.outputStepDatas.find(
      (stepData) => stepData.name === RegressionStepDataName.MODEL_VARIABLES
    )?.dataFrames[0]?.id ?? null;

  return (
    <div
      {...getTestProps({component: 'EditRegressionView', element: 'root'})}
      className={css(visualizationEditorSkeletonStyles.view)}
    >
      <RegressionQueryingVisualizationEditorSkeleton
        analysis={analysis}
        clientStateId={clientStateId}
        inputDataFrameId={inputDataFrameId}
        modelOutputDataFrameId={modelOutputDataFrameId}
        modelVariablesDataFrameId={modelVariablesDataFrameId}
        onChangeVisualizationConfig={handleChangeVisualizationConfig}
        onChangeVisualizationName={handleChangeVisualizationName}
        regressionStep={regressionStep}
        renderFromChart={hasChartOutput}
        renderEditorSidebarChildren={(
          inputDataFrame: VisualizationStepEditorDataFrameNode,
          invalidatePreview: () => void
        ) =>
          renderEditorSidebarChildren(
            {
              analysis,
              clientStateId,
              regressionStep,
              inputDataFrameId,
              visualizationConfig,
              regressionStepEditorState,
              setRegressionStepEditorState,
              viewSteps,
              canAddNewSteps,
              addNewStepsDisabledMessage,
              draftRegressionConfig,
            },
            inputDataFrame,
            invalidatePreview
          )
        }
        shouldDisplayRegressionLines={regressionStepEditorState.shouldDisplayRegressionLines}
        sidebarProps={{
          doneButtonIsDisabled: isInvalidRegressionName(regressionName),
          onClickDoneButton: handleCloseRegression,
          typeSelectorIsDisabled: true,
          enableAnalysisTab: false,
          enableFormatTab: false,
          visualizationEditorIsDisabled: regressionStepEditorState.isReadOnly,
        }}
        viewSteps={viewSteps}
        canAddNewSteps={canAddNewSteps}
        visualizationConfig={visualizationConfig}
        visualizationName={regressionName}
        visualizationSaveState={regressionStepEditorState.saveState}
        warnings={
          isDuplicateName(analysis, regressionStep, regressionName)
            ? ['Regression name already exists. This regression will be saved with a suffix.']
            : undefined
        }
      />
    </div>
  );
}
